/**
 * SettingsShell — shared tab wrapper for all Settings sub-screens.
 * Tabs: General · Integrations (others stubbed). Team & roles was merged into
 * Staff & Roles (route `people`) and retired 2026-08-10.
 */
import { useEffect } from "react";
import { Screen } from "../../shell/chrome";
import { can, isAdmin } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import type { ReactNode } from "react";
import { useT } from "../../lib/i18n";
import { moduleKnownAbsent, useTenantModules } from "../../lib/tenant-modules";

export const SETTINGS_TABS_DEF = [
  { id: "general",   label: "General",          labelKey: "settings.shell.general",      perm: "settings.read",     route: "settings.general" },
  // Branding moved to a top-level rail item (Greg 2026-07-07 item 13) — no longer a Settings tab.
  { id: "closures",  label: "Closed days",       labelKey: "settings.shell.closedDays",   perm: "settings.read",     route: "settings.closures" },
  { id: "import",    label: "Import",            labelKey: "common.actions.import",        perm: "settings.read",     route: "settings.import" },
  { id: "integ",     label: "Integrations",      labelKey: "settings.shell.integrations", perm: "integrations.read", route: "settings.integ" },
  /* Customer types (2026-09-08, kit e03f10e9) — company-scoped classification
     of customer records, gated on its own capability family rather than the
     blanket `settings.read`, because `customer_types.read` is what the
     operations actually check. */
  { id: "customerTypes", label: "Customer types", labelKey: "settings.shell.customerTypes", perm: "customer_types.read", route: "settings.customerTypes" },
  { id: "calendar",  label: "Calendar",          labelKey: "settings.shell.calendar",     perm: "settings.read",     route: "settings.calendar" },
  // Manage links (2026-08-31) — the short-link half of the manage-settings privacy
  // group. First consumer of getManageSettings/updateManageSettings in this app.
  { id: "manageLinks", label: "Manage links",     labelKey: "settings.shell.manageLinks",  perm: "settings.read",     route: "settings.manageLinks" },
  { id: "approvals", label: "Approvals",         labelKey: "settings.shell.approvals",    perm: "settings.read",     route: "settings.approvals" },
  /* AI (CC 0120, 2026-10-01): gated on settings.write, a capability the backend grants.
     It was adminOnly (admin.super), which no deployed role has, so the tab never showed. */
  { id: "ai",        label: "AI",                labelKey: "settings.shell.ai",           perm: "settings.write",    route: "settings.ai" },
  { id: "billing",   label: "Billing",           labelKey: "settings.shell.billing",      perm: "billing.read",      route: "settings.billing" },
  // Tax rates & config (2026-08-04) — payments admin surface, same billing.read gate.
  { id: "tax",       label: "Tax",               labelKey: "settings.shell.tax",          perm: "billing.read",      route: "settings.tax" },
  { id: "security",  label: "Security",          labelKey: "settings.shell.security",     perm: "settings.read",     route: "settings.security" },
  { id: "audit",     label: "Audit log",         labelKey: "settings.shell.auditLog",     perm: "settings.read",     route: "settings.audit" },
  { id: "api",       label: "API activity",      labelKey: "settings.shell.apiActivity",  perm: "observability.read", route: "settings.api" },
  // Advanced (factory reset) tab RETIRED 2026-07-10 — factory reset was its
  // only content, so per the docket's own instruction it moved into General's
  // Danger zone (gated the same way, on `settings.factory_reset`) rather than
  // keeping a single-purpose tab around.
] as const;

export default function SettingsShell({ activeTab, children }: { activeTab: string; children: ReactNode }) {
  const t = useT();
  const nav = useNav();
  /* Billing and Tax only on a tenant with payments (CC 0130). Manage links only on a tenant with booking
     (CC, 2026-10-04): its settings read and write are served by the booking worker, so a tenant without
     booking answered 503 and the tab opened on an error. Unknown modules (loading, or the read failed)
     keep every tab, as before. */
  const mods = useTenantModules();
  const tabs = SETTINGS_TABS_DEF
    .filter((tab) => !tab.perm || can(tab.perm))
    .filter((tab) => !((tab.id === "billing" || tab.id === "tax") && moduleKnownAbsent("payments", mods)))
    .filter((tab) => !(tab.id === "manageLinks" && moduleKnownAbsent("booking", mods)))
    .filter((tab) => !(tab as { adminOnly?: boolean }).adminOnly || isAdmin())
    .map((tab) => ({ id: tab.id, label: t(tab.labelKey, tab.label), onClick: () => nav.go(tab.route) }));

  useEffect(() => {
    if (tabs.length === 0) {
      toast({ tone: "warn", title: t("settings.shell.settingsUnavailable", "Settings unavailable"), subtitle: t("settings.shell.yourRoleIsReadOnlyAndCanTAccessWorkspaceSettings", "Your role is read-only and can't access workspace settings.") });
      nav.replace("events");
    }
  }, [tabs.length]);

  if (tabs.length === 0) return null;
  const visibleActiveTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : tabs[0].id;
  return (
    <Screen rail="settings" tabs={tabs} activeTab={visibleActiveTab}>
      {children}
    </Screen>
  );
}
